import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { fetchAuthSession } from 'aws-amplify/auth';
import { LoggerService } from '../shared/services/logger.service';
import { ROUTES } from '../shared/constants/app.constants';
import { Router } from '@angular/router';
export interface UserRole {
  sub: string;
  email?: string;
  groups: string[];
  isAdmin: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly userRoleSubject = new BehaviorSubject<UserRole | null>(null);

  // ✅ cache della sessione per evitare chiamate concorrenti
  private sessionPromise: Promise<string | null> | null = null;

  public userRole$: Observable<UserRole | null> = this.userRoleSubject.asObservable();
  public isAdmin$: Observable<boolean> = this.userRole$.pipe(map(role => role?.isAdmin ?? false));
  public groups$: Observable<string[]> = this.userRole$.pipe(map(role => role?.groups ?? []));

  constructor(
    private readonly logger: LoggerService,
    private readonly router: Router
  ) {}

async initFromSession(): Promise<void> {
  try {
    const token = await this.getTokenFromSession();
    if (!token || this.isTokenExpired(token)) {
      this.clearUserRole();
      return;
    }
    this.setUserRoleFromToken(token);

  } catch (e: any) {
    // Amplify throws this when no user is signed in — not a real error
    if (e?.name === 'UserUnAuthenticatedException' || 
        e?.name === 'NotAuthorizedException' ||
        e?.message?.includes('No current user')) {
      this.logger.debug('[AUTH] No active session found');
    } else {
      this.logger.error('[AUTH] Unexpected error during session init', e);
    }
    this.clearUserRole();
  }
}

  

  getTokenFromStorage(): string | null {
    const keys = Object.keys(localStorage);
    const idTokenKey = keys.find(k => k.endsWith('.idToken'));
    return idTokenKey ? localStorage.getItem(idTokenKey) : null;
  }

  decodeToken(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Gestione corretta Base64Url per JWT
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  setUserRoleFromToken(token: string): void {
    const payload = this.decodeToken(token);
    if (!payload) {
      this.clearUserRole();
      return;
    }

    const groups = this.extractGroupsFromToken(payload);
    const userRole: UserRole = {
      sub: payload.sub || '',
      email: payload.email,
      groups,
      isAdmin: groups.includes('admin'),
    };

    this.userRoleSubject.next(userRole);
  }

  extractGroupsFromToken(payload: any): string[] {
    if (!payload) return [];
    const groups = payload['cognito:groups'];
    return Array.isArray(groups) ? groups : [];
  }

  clearUserRole(): void {
    this.userRoleSubject.next(null);
  }

  isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload?.exp) return true;
    return Date.now() >= payload.exp * 1000;
  }
  async checkExistingSession(): Promise<void> {
    const token = await this.getTokenFromSession();
    if (token && !this.isTokenExpired(token)) {
      this.logger.info(`[AUTH] Valid token found`);
      this.setUserRoleFromToken(token);
      this.router.navigateByUrl(ROUTES.DASHBOARD || '/dashboard');
    }
  }


  // Deduplicazione chiamate concorrenti: la promise è condivisa finché è in-flight,
  // poi resettata subito così la prossima chiamata ottiene un token fresco da Amplify.
  async getTokenFromSession(): Promise<string | null> {
    if (!this.sessionPromise) {
      this.sessionPromise = fetchAuthSession()
        .then(session => session.tokens?.idToken?.toString() ?? null)
        .catch(() => null)
        .finally(() => { this.sessionPromise = null; });
    }
    return this.sessionPromise;
  }
  
}